import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Users, ArrowRight, X, Check, BookOpen, AlertCircle } from 'lucide-react';
import { FALLBACK_COURSES } from '../data';

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    message: '',
    website_url: '' // Honeypot
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    async function fetchPage() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/by-slug/da/kursus');
        if (res.ok) {
          const data = await res.json();
          setPageData(data);
        }
      } catch (err) {
        console.error('Error fetching courses page:', err);
      }
    }
    fetchPage();
  }, []);

  useEffect(() => {
    async function fetchCourses() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/courses');
        if (res.ok) {
          const data = await res.json();
          if (data && data.items && data.items.length > 0) {
            // Filtrer kurser så kun dem med udgivet === true vises
            const publishedItems = data.items.filter(item => item.udgivet === true);
            const mapped = publishedItems.map(item => ({
              id: item.id,
              title: item.title?.da || item.title || '',
              date: item.date?.da || item.date || '',
              target_audience: item.target_audience?.da || item.target_audience || '',
              duration: item.duration?.da || item.duration || '',
              description: item.description?.da || item.description || ''
            }));
            setCourses(mapped);
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        console.error('Error fetching courses from CMS:', err);
      }
      setCourses(FALLBACK_COURSES);
      setLoading(false);
    }
    fetchCourses();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openModal = (course) => {
    setSelectedCourse(course);
    setFormData({
      name: '',
      phone: '',
      email: '',
      company: '',
      message: '',
      website_url: ''
    });
    setSubmitSuccess(false);
    setSubmitError('');
  };

  const closeModal = () => {
    setSelectedCourse(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          courseId: selectedCourse.id,
          courseTitle: selectedCourse.title,
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          company: formData.company,
          message: formData.message,
          website_url: formData.website_url // Honeypot
        })
      });

      if (response.ok) {
        setSubmitSuccess(true);
      } else {
        setSubmitError('Der opstod en fejl ved afsendelse af tilmeldingen. Prøv venligst igen.');
      }
    } catch (err) {
      setSubmitError('Netværksfejl. Kontroller din internetforbindelse og prøv igen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = pageData?.title?.da || pageData?.title || "Kurser & Uddannelse - CARPA ApS";
  const desc = pageData?.meta_description?.da || pageData?.meta_description || "Tilmeld dig vores faglige kurser inden for strategisk vedligehold, commissioning, operational readiness og safety. Vi styrker dine tekniske kompetencer.";

  return (
    <div className="bg-[#f7f8fa] text-[#0e1f2f] min-h-screen font-sans antialiased">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[55vh] lg:min-h-[60vh] flex flex-col justify-center py-16 md:py-24 overflow-hidden bg-[#0a1520] border-b border-[#0e1f2f]/20 mb-16">
        {/* Background Grid Pattern & Image Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/59acd1b6-4973-4db5-aba9-2022467ddb50/594540e3-6717-499c-be64-92e37498def2.jpg"
            alt="Kurser"
            className="h-full w-full object-cover opacity-35 lg:opacity-45 object-center"
          />
          {/* Subtle Technical Grid Overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(23,211,214,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(23,211,214,0.04)_1px,transparent_1px)] bg-[size:24px_24px]" />
          {/* Gradient Overlay preserving text legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1520] via-[#0a1520]/95 to-transparent hidden lg:block" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1520]/90 via-[#0a1520]/80 to-[#0a1520] lg:hidden" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Title & Process */}
            <div className="lg:col-span-7 space-y-6 text-left">
              {/* Action/Process Header Link */}
              <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm font-mono tracking-wider uppercase">
                <span className="text-[#17d3d6] font-bold">
                  {pageData?.hero_badge?.da || pageData?.hero_badge || "Uddan → Styrk → Optimer Kompetencer"}
                </span>
              </div>

              <div className="space-y-2">
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-wide uppercase text-white leading-none">
                  {pageData?.hero_title?.da || pageData?.hero_title || "Kurser & Uddannelse"}
                </h1>
                <div className="w-16 h-[3px] bg-[#17d3d6] mt-4" />
              </div>

              <p className="text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl">
                {pageData?.hero_description?.da || pageData?.hero_description || "Tilmeld dig vores faglige kurser inden for strategisk vedligehold, commissioning, operational readiness og safety. Vi styrker dine tekniske kompetencer."}
              </p>
            </div>

            {/* Right Column: Premium Floating Card with Glow Border */}
            <div className="lg:col-span-5">
              <div className="bg-[#041326]/65 backdrop-blur-md border border-[rgba(23,211,214,0.15)] rounded-xl p-6 lg:p-8 flex flex-col justify-between transition-all duration-300 hover:border-[#17d3d6]/40 shadow-2xl">
                <div>
                  <div className="flex items-center gap-4 pb-4 border-b border-slate-700/40">
                    <div className="w-12 h-12 shrink-0 rounded-full border border-[#17d3d6] flex items-center justify-center text-[#17d3d6]">
                      <BookOpen className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-white text-lg font-bold leading-tight">Uddannelsesinfo</h3>
                      <p className="text-xs text-slate-400 font-mono tracking-wider uppercase mt-0.5">CARPA ApS</p>
                    </div>
                  </div>

                  <ul className="mt-6 space-y-3.5">
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      Praksisnære og generiske kurser
                    </li>
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      Fokus på strategisk vedligehold & CMMS
                    </li>
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      Skræddersyede virksomhedsforløb
                    </li>
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      Erfarne undervisere direkte fra marken
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Grid Section */}
      <section className="py-12 md:py-20">
        <div className="mx-auto max-w-7xl px-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#0e1f2f]/10 border-t-[#3c8888]" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
              {courses.map((course) => (
                <div 
                  key={course.id}
                  className="flex flex-col rounded-xl border border-[#0e1f2f]/10 bg-white p-6 md:p-8 shadow-sm hover:border-[#3c8888]/30 hover:shadow-md transition-all duration-300"
                >
                  <div className="space-y-4 flex-1">
                    {/* Badge & Title */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs font-mono text-[#3c8888] font-bold uppercase tracking-wider">
                        <BookOpen className="h-4 w-4" />
                        <span>Kursus</span>
                      </div>
                      <h2 className="text-2xl font-bold text-[#0e1f2f] tracking-tight">
                        {course.title}
                      </h2>
                    </div>

                    {/* Meta details */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-3 border-y border-[#0e1f2f]/10 text-xs text-stone-600 font-mono">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-[#3c8888]" />
                        <span>Dato: {course.date}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-[#3c8888]" />
                        <span>Varighed: {course.duration}</span>
                      </div>
                      <div className="flex items-start gap-2 sm:col-span-2">
                        <Users className="h-4 w-4 text-[#3c8888] shrink-0 mt-0.5" />
                        <span>Målgruppe: {course.target_audience}</span>
                      </div>
                    </div>

                    {/* Description */}
                    <div 
                      className="text-stone-600 text-sm leading-relaxed space-y-3 [&_p]:leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: course.description.startsWith('<') ? course.description : `<p>${course.description}</p>` }}
                    />
                  </div>

                  {/* CTA Button */}
                  <div className="pt-6 mt-6 border-t border-[#0e1f2f]/10">
                    <button
                      onClick={() => openModal(course)}
                      className="w-full sm:w-auto inline-flex items-center justify-center rounded-md bg-[#3c8888] px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-[#3c8888]/90 transition-colors cursor-pointer border-b-2 border-[#3c8888]"
                    >
                      <span>Tilmeld dig her</span>
                      <ArrowRight className="h-4 w-4 ml-1.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Course Info Block */}
      <section className="py-12 bg-white border-t border-[#0e1f2f]/10">
        <div className="mx-auto max-w-4xl px-6 text-center space-y-6">
          <h2 className="text-2xl font-bold text-[#0e1f2f] tracking-tight">
            Skræddersyede virksomhedskurser?
          </h2>
          <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
            Vi tilbyder også at tilpasse vores kurser direkte til jeres virksomheds specifikke behov, anlæg og systemer. Kurserne kan afholdes on-site hos jer eller i vores lokaler.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center justify-center rounded-md bg-[#0e1f2f] px-6 py-3 text-sm font-bold text-white shadow-sm hover:bg-[#0e1f2f]/90 transition-colors border-b-2 border-[#3c8888]"
          >
            Kontakt os for et tilbud
          </Link>
        </div>
      </section>

      {/* Tilmeldingsmodal */}
      {selectedCourse && (
        <div className="fixed inset-0 bg-black/60 flex items-start justify-center p-4 pt-24 md:pt-32 z-50 overflow-y-auto">
          <div className="relative w-full max-w-[600px] bg-white rounded-2xl p-6 md:p-8 shadow-2xl flex flex-col gap-6 mb-12">
            
            {/* Close Button */}
            <button 
              onClick={closeModal}
              className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center border border-stone-200 rounded-full hover:bg-stone-50 transition-colors cursor-pointer" 
              aria-label="Luk"
            >
              <X className="w-4 h-4 text-stone-500" />
            </button>

            {/* Header */}
            <div className="space-y-2">
              <span className="text-xs font-mono text-[#3c8888] font-bold uppercase tracking-wider block">Tilmeldingsformular</span>
              <h2 className="text-2xl font-bold text-[#0e1f2f] leading-tight pr-10">
                {selectedCourse.title}
              </h2>
              <p className="text-xs text-stone-500 font-mono">
                Varighed: {selectedCourse.duration} · Dato: {selectedCourse.date}
              </p>
            </div>

            {submitSuccess ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center space-y-4">
                <div className="mx-auto h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                  <Check className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-bold text-emerald-800">Tak for din tilmelding!</h3>
                <p className="text-sm text-emerald-700">
                  Vi har modtaget din tilmelding til <strong>{selectedCourse.title}</strong>. Vi vender tilbage til dig hurtigst muligt med en bekræftelse og yderligere detaljer.
                </p>
                <button
                  onClick={closeModal}
                  className="mt-2 inline-flex items-center justify-center rounded-md bg-[#3c8888] px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-[#3c8888]/90 transition-colors cursor-pointer border-b-2 border-[#3c8888]"
                >
                  Luk vindue
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Honeypot field (Antispam) */}
                <div className="sr-only absolute overflow-hidden h-0 w-0" aria-hidden="true">
                  <label htmlFor="website_url">Do not fill this out</label>
                  <input
                    id="website_url"
                    type="text"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleInputChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                {/* Navn */}
                <div className="space-y-1.5">
                  <label className="text-xs font-mono uppercase tracking-wider text-stone-500 font-bold">Navn *</label>
                  <input 
                    type="text" 
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Dit fulde navn"
                    className="w-full px-4 py-2.5 border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3c8888]/30 transition-all text-sm" 
                  />
                </div>

                {/* Split Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Telefon */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-mono uppercase tracking-wider text-stone-500 font-bold">Telefon *</label>
                    <input 
                      type="tel" 
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+45 00 00 00 00"
                      className="w-full px-4 py-2.5 border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3c8888]/30 transition-all text-sm" 
                    />
                  </div>
                  {/* Email */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-mono uppercase tracking-wider text-stone-500 font-bold">E-mail *</label>
                    <input 
                      type="email" 
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="navn@virksomhed.dk"
                      className="w-full px-4 py-2.5 border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3c8888]/30 transition-all text-sm" 
                    />
                  </div>
                </div>

                {/* Virksomhed */}
                <div className="space-y-1.5">
                  <label className="text-xs font-mono uppercase tracking-wider text-stone-500 font-bold">Virksomhed</label>
                  <input 
                    type="text" 
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Virksomhedsnavn"
                    className="w-full px-4 py-2.5 border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3c8888]/30 transition-all text-sm" 
                  />
                </div>

                {/* Meddelelse */}
                <div className="space-y-1.5">
                  <label className="text-xs font-mono uppercase tracking-wider text-stone-500 font-bold">Eventuelle bemærkninger</label>
                  <textarea 
                    name="message"
                    rows="3" 
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Skriv hvis du har særlige ønsker eller spørgsmål..."
                    className="w-full px-4 py-2.5 border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3c8888]/30 transition-all text-sm resize-none"
                  />
                </div>

                {submitError && (
                  <div className="flex items-center gap-2 text-sm text-red-600 font-semibold bg-red-50 p-3 rounded-md border border-red-100">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>{submitError}</span>
                  </div>
                )}

                {/* Submit Button */}
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full py-3 bg-[#3c8888] text-white font-bold tracking-wider text-sm uppercase rounded-md hover:bg-[#3c8888]/90 transition-colors focus:outline-none focus:ring-4 focus:ring-[#3c8888]/30 disabled:opacity-50 cursor-pointer border-b-2 border-[#3c8888]"
                >
                  {isSubmitting ? 'Sender tilmelding...' : 'Send tilmelding'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
